import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Select, Space, Tag } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { areaLib } from "@/utils/areaLib";
import { useAreaLib } from "@/utils/hooks/useAreaLib";
import { dateTimeFormat } from "@/utils";
import { useUserStore } from "@/stores/react/authStore";

export interface OilSite {
    id: string | number;
    name?: string;
    brand?: string;
    address?: string;
    provinceCode?: string | number;
    cityCode?: string | number;
    regionCode?: string | number;
    createdTime?: string;
    state?: number;
}

export interface OilSitePageParams {
    page: number;
    size: number;
    keyword?: string;
    brand?: string;
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface OilSitePageProps {
    loadSites?: (params: OilSitePageParams) => Promise<{ list: OilSite[]; total: number }>;
    disableSite?: (id: string | number) => Promise<unknown>;
    enableSite?: (id: string | number) => Promise<unknown>;
    deleteSite?: (id: string | number) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 站点分页接口。 */
export async function loadOilSites(params: OilSitePageParams) {
    const response = await shippingRequestHttp.service.get<{ list?: OilSite[]; total?: number }>("/oil-site/paging", {
        params
    });
    return { list: response.data.list || [], total: response.data.total || 0 };
}

/** 复用 Vue 站点状态更新接口。 */
export async function updateOilSiteState(id: string | number, state: number) {
    const response = await shippingRequestHttp.service.post(`/oil-site/update/${id}/${state}`);
    return response.data;
}

/** 复用 Vue 站点删除接口。 */
export async function deleteOilSite(id: string | number) {
    const response = await shippingRequestHttp.service.delete(`/oil-site/${id}`);
    return response.data;
}

/** 点击时复用 Vue 服务层，保留启停二次确认和成功提示，避免列表模块初始化服务实例。 */
async function disableOilSite(id: string | number) {
    const { oilSiteService } = await import("@/api/services/oilSiteService");
    return oilSiteService.disable(id);
}

/** 点击时复用 Vue 服务层，保留启停二次确认和成功提示，避免列表模块初始化服务实例。 */
async function enableOilSite(id: string | number) {
    const { oilSiteService } = await import("@/api/services/oilSiteService");
    return oilSiteService.enable(id);
}

/** 将站点编号等未知值收敛为表格可渲染文本。 */
const text = (value: unknown) => (value == null || value === "" ? "-" : String(value));
const areaCode = (value: string | number | undefined) => (value === undefined ? undefined : String(value));

/** 加油站点列表，保留 Vue 筛选、状态枚举和操作入口。 */
export function OilSitePage({
    loadSites = loadOilSites,
    // 沿用 Vue oilSiteService 的确认装饰器，避免默认启停操作绕过二次确认。
    disableSite = disableOilSite,
    enableSite = enableOilSite,
    deleteSite = deleteOilSite,
    permissions
}: OilSitePageProps) {
    // 订阅 Vue areaLib 数据加载完成事件，渲染时继续使用同一个全局地区库实例。
    useAreaLib();
    const navigate = useNavigate();
    const storePermissions = useUserStore(user => user.userInfo.permissions || []);
    const banButton = useUserStore(user => user.banButton);
    // 保持 Vue v-auth 语义：显式测试权限优先，否则读取当前登录用户权限。
    const canWrite = (permissions ?? storePermissions).includes("ROLE_GAS_STATION_W");
    const [sites, setSites] = useState<OilSite[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    // 对齐旧 Vue ProTable，保留当前分页条数并在切换时提交到查询接口。
    const [pageSize, setPageSize] = useState(10);
    const [keyword, setKeyword] = useState("");
    const [brand, setBrand] = useState("");
    const [state, setState] = useState<number>();
    const [sortOrder, setSortOrder] = useState<string>();
    const [errorMessage, setErrorMessage] = useState("");

    const load = useCallback(async () => {
        try {
            const result = await loadSites({
                page,
                size: pageSize,
                keyword: keyword || undefined,
                brand: brand || undefined,
                state,
                ...(sortOrder ? { sortKey: "created_time", sortOrder } : {})
            });
            setSites(result.list);
            setTotal(result.total);
            setErrorMessage("");
        } catch {
            setErrorMessage("站点列表加载失败");
        }
    }, [brand, keyword, loadSites, page, pageSize, sortOrder, state]);

    // 对齐旧 Vue createdTime 自定义排序参数，避免只改变表头而未改变后端排序结果。
    const handleSortChange = ({ prop, order }: { prop?: string; order?: string | null }) => {
        setSortOrder(prop === "createdTime" && order ? order : undefined);
        setPage(1);
    };

    useEffect(() => {
        void load();
    }, [load]);

    const runAction = useCallback(
        async (action: () => Promise<unknown>) => {
            try {
                await action();
                await load();
            } catch {
                setErrorMessage("站点操作失败");
            }
        },
        [load]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 加油站点 ZhTable 的字段和 operation 插槽。
    const siteColumns = useMemo<TableColumnProps<OilSite>[]>(
        () => [
            {
                key: "id",
                label: "编号",
                width: 80,
                prop: "id",
                render: ({ row }) => <Link to={`/logistics/oil/OilSiteDetail/${row.id}`}>{text(row.id)}</Link>
            },
            {
                key: "name",
                label: "站点名称",
                minWidth: 120,
                prop: "name",
                render: ({ row }) => <Link to={`/logistics/oil/OilSiteDetail/${row.id}`}>{row.name || "-"}</Link>
            },
            { key: "brand", label: "品牌", width: 190, prop: "brand", render: ({ row }) => row.brand || "-" },
            {
                key: "address",
                label: "地址",
                minWidth: 160,
                prop: "address",
                render: ({ row }) =>
                    areaLib.getFullAreaName(
                        areaCode(row.provinceCode),
                        areaCode(row.cityCode),
                        areaCode(row.regionCode)
                    ) ||
                    row.address ||
                    "-"
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: 154,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => (row.createdTime ? dateTimeFormat(row.createdTime) : "-")
            },
            {
                key: "state",
                label: "状态",
                width: 92,
                prop: "state",
                render: ({ row }) => <Tag>{Number(row.state) === 1 ? "启用" : "禁用"}</Tag>
            },
            {
                key: "operation",
                label: "操作",
                width: 260,
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    canWrite ? (
                        <Space>
                            <Button
                                aria-label="修改"
                                disabled={banButton}
                                size="small"
                                onClick={() => navigate(`/logistics/oil/oilSiteEdit/${row.id}`)}
                            >
                                修改
                            </Button>
                            {row.state === 1 ? (
                                <Button
                                    aria-label="禁用"
                                    disabled={banButton}
                                    size="small"
                                    onClick={() => void runAction(() => disableSite(row.id))}
                                >
                                    禁用
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        aria-label="启用"
                                        disabled={banButton}
                                        size="small"
                                        onClick={() => void runAction(() => enableSite(row.id))}
                                    >
                                        启用
                                    </Button>
                                    <Button
                                        aria-label="删除"
                                        disabled={banButton}
                                        size="small"
                                        onClick={() => void runAction(() => deleteSite(row.id))}
                                    >
                                        删除
                                    </Button>
                                </>
                            )}
                        </Space>
                    ) : null
            }
        ],
        [banButton, canWrite, deleteSite, disableSite, enableSite, navigate, runAction]
    );

    return (
        <section aria-label="oil-site-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space wrap>
                    <Input
                        aria-label="站点关键词"
                        placeholder="请输入站点名称或编号"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    <Input
                        aria-label="品牌"
                        placeholder="请输入品牌搜索"
                        value={brand}
                        onChange={event => setBrand(event.target.value)}
                    />
                    <Select
                        aria-label="状态"
                        allowClear
                        placeholder="请选择状态"
                        value={state}
                        options={[
                            { value: 1, label: "启用" },
                            { value: 2, label: "禁用" }
                        ]}
                        onChange={value => setState(value)}
                    />
                    <Button type="primary" onClick={() => void load()}>
                        搜索
                    </Button>
                    <Button
                        onClick={() => {
                            setKeyword("");
                            setBrand("");
                            setState(undefined);
                            setPage(1);
                        }}
                    >
                        重置
                    </Button>
                    {canWrite ? (
                        <Button
                            disabled={banButton}
                            type="primary"
                            onClick={() => navigate("/logistics/oil/oilSiteAdd")}
                        >
                            新增
                        </Button>
                    ) : null}
                </Space>
            </Card>
            <Card>
                <ZhTable<OilSite>
                    rowKey="id"
                    data={sites}
                    columns={normalizeOperationColumns(siteColumns, sites)}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={page}
                                pageSize={pageSize}
                                total={total}
                                hideOnSinglePage
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(nextPage, nextPageSize) => {
                                    // 旧版 ProTable 切换条数后固定回到第 1 页，翻页则保留当前条数。
                                    setPage(nextPageSize !== pageSize ? 1 : nextPage);
                                    setPageSize(nextPageSize);
                                }}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default OilSitePage;
